import { useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { generatePagerProps } from '@fukuri-admins/utils/common/generatePagerProps';
import { Modal } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { getClients } from '~/api/clients';
import { createPushNotifications } from '~/api/notifications/index.ts';
import { useAuth } from '~/components/auth/AuthProvider.tsx';
import { ActionButton } from '~/components/common/ActionButton';
import { ClientType, clientSearchParams } from '~/types/resources/ClientType';
import { NotificationForm, NotificationParams } from '~/types/resources/Notification';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '~/types/table/ActionTableSortParamsType.ts';
import { getPushNotificationLink } from '~/utils/getLinks';
import { CreateForm } from './CreateForm';
import styles from './styles.module.scss';

export const CreatePushNotification = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchClientsData, setFetchClientsData] = useState<ClientType[]>([]);
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>(
    defaultActionTableSortParams
  );
  const [totalCount, setTotalCount] = useState<number>(0);
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<NotificationForm | null>(null);
  const [errMsg, setErrMsg] = useState<string>('');
  // 企業名検索・ページングでは選択済みの企業名を一覧の外（Chip）に表示する必要があり、
  // ページを移動すると fetchClientsData から消えて ID から名前を引けなくなる。
  // そのため選択状態は ID ではなく企業そのものを保持する
  const [selectedClients, setSelectedClients] = useState<ClientType[]>([]);
  // 表示中のページ・検索語。どちらも取得成功時にだけ更新する。
  // 「取得したい条件」として先に進めると、取得に失敗したときに条件だけが変わって
  // 表の中身と食い違う（ページャーの番号・表示範囲が進む / 次のページ送りが
  // 一度も読み込めていない検索語で飛ぶ）
  const [loadedPage, setLoadedPage] = useState<number>(1);
  const [loadedName, setLoadedName] = useState<string>('');
  const [totalPage, setTotalPage] = useState<number>(0);
  // 取得の世代番号。後述の getClientData で後着レスポンスの追い越しを検出するために使う
  const requestSeqRef = useRef<number>(0);

  const getClientData = async (page: number, name: string) => {
    // 検索バーはローディング中も操作できるため、取得中に次の取得が始まりうる。
    // 世代番号で最新以外のレスポンスを破棄しないと、遅い先発が後発を上書きして一覧がズレる
    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    try {
      // 一覧取得はサーバー側で 300 件（fukuri-core の MAX_LIMIT_COUNT）に切り詰められる。
      // limit を上げても無視されるため、ページングで全件へ到達させる。
      // name は部分一致検索（BE 側で sanitize_sql_like 済み）。空文字のときはキーごと送らない
      const searchParams: clientSearchParams = {
        page: String(page),
        enabled: '1',
        ...(name && { name }),
      };

      // アクティブなパートナー企業のみを取得する
      const { data, headers } = await getClients(signOut, searchParams);
      if (seq !== requestSeqRef.current) return;
      setTotalCount(Number(headers['total-count']));
      setTotalPage(Number(headers['total-pages']));
      const clientsData = data.clients;
      setFetchClientsData(clientsData);
      // 一覧と同じ commit で確定させる。別経路で更新すると表示がズレる
      setLoadedPage(page);
      setLoadedName(name);
    } catch {
      if (seq !== requestSeqRef.current) return;
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      // 追い越された取得はスピナーを消さない。消すと後発の取得中に一覧が一瞬表示される
      if (seq === requestSeqRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleFormSubmit = (data: NotificationForm) => {
    setFormData({ ...data, target_client_ids: selectedClients.map(client => client.id) });
    setIsOpen(true);
  };

  const omit = <T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> => {
    const ret = { ...obj };
    keys.forEach(key => {
      delete ret[key];
    });
    return ret;
  };

  const onSubmit = async () => {
    try {
      // 手動選択の場合のみ、対象企業のチェックを行う
      if (formData?.deliveryTarget === 'manual' && formData?.target_client_ids.length === 0) {
        setErrMsg('対象企業が選択されていません');
        return;
      }

      // グループ選択の場合、グループが選択されているかチェック
      if (formData?.deliveryTarget === 'group' && !formData?.selectedGroupId) {
        setErrMsg('グループが選択されていません');
        return;
      }

      // immediateの場合はscheduled_datetimeをnullにする
      const scheduled_datetime =
        formData.isImmediate === 'scheduled' && formData.selectedDate && formData.selectedTime
          ? `${formData.selectedDate}T${String(formData.selectedTime).padStart(2, '0')}:${formData.selectedMinute}:00`
          : null;

      const rest = omit(formData, [
        'selectedDate',
        'selectedTime',
        'selectedMinute',
        'target_client_ids',
        'isImmediate',
        'deliveryTarget',
        'selectedGroupId',
        'excludedTargets',
      ]);

      const payload: NotificationParams = {
        ...rest,
        scheduled_datetime,
        category: 'none',
        selected_option: formData.isImmediate,
        ...(formData.excludedTargets?.includes('family_users') && {
          target_user_type: 'employees_only',
        }),
      };

      // 配信先に応じてgroup_idまたはtarget_client_idsを設定
      if (formData.deliveryTarget === 'group' && formData.selectedGroupId) {
        payload.group_id = Number(formData.selectedGroupId);
      } else if (formData.deliveryTarget === 'manual' && formData.target_client_ids?.length > 0) {
        payload.target_client_ids = formData.target_client_ids;
      }
      // 'all'の場合はgroup_idもtarget_client_idsも送らない

      await createPushNotifications(payload);
      navigate(getPushNotificationLink());
    } catch (error) {
      if (error) {
        setErrMsg(error.details?.join(', '));
      } else {
        setErrMsg('An unexpected error occurred while creating the notification.');
      }
    }
  };

  // 初回取得のみ。ページ送り・検索は state 経由ではなくハンドラから getClientData を直接呼ぶ。
  // 「取得したい条件」を state に置いて effect で追いかける形にすると、
  // 取得に失敗したときにその state だけが進んで表示とズレる。
  // さらに失敗後に同じページ・同じ検索語をもう一度指定しても state が変化せず
  // effect が再発火しないため、リトライもできなくなる
  // getClientData は毎レンダー再生成されるため依存配列には入れない（入れると毎レンダー再取得になる）
  // 取得の追い越しは getClientData 側の世代番号で弾いている
  useEffect(() => {
    getClientData(1, '');
  }, []);

  // ページ送りは表示中の検索語のまま行う
  const handlePageChange = (page: number) => getClientData(page, loadedName);

  // 検索は常に 1 ページ目から取得する。
  // 絞り込み後の総ページ数を現在のページ番号が超えていると一覧が空になるため
  const handleSearch = (name: string) => getClientData(1, name);

  // 1 ページあたりの件数はレスポンスヘッダーに無いため、
  // 取得件数と総件数から generatePagerProps に逆算させる（features/ 配下の一覧と同じ組み立て方）
  const pagerProps = generatePagerProps({
    currentPage: loadedPage,
    totalPage,
    totalCount,
    rows: fetchClientsData,
    onPageChange: handlePageChange,
  });

  return (
    <div className={styles.Root} data-testid='client-page'>
      <CreateForm
        fetchClientsData={fetchClientsData}
        setFetchClientsData={setFetchClientsData}
        sortParams={sortParams}
        setSortParams={setSortParams}
        isLoading={isLoading}
        handleFormSubmit={handleFormSubmit}
        selectedClients={selectedClients}
        setSelectedClients={setSelectedClients}
        pagerProps={pagerProps}
        onSearch={handleSearch}
      />
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <div className={styles.modalContent}>
          <div className={styles.errorMessage} data-testid='edit-error-messages'>
            {errMsg}
          </div>
          <div className={styles.modalDescription}>
            {formData?.deliveryTarget === 'all' && 'すべての企業に送信されます。'}
            {formData?.deliveryTarget === 'group' && 'グループに所属する企業に送信されます。'}
            {formData?.deliveryTarget === 'manual' &&
              (formData.target_client_ids.length > 0
                ? `${formData.target_client_ids.length}件の企業に送信されます。`
                : '対象企業が選択されていません。')}
            <br />
            {formData &&
              (formData.deliveryTarget !== 'manual' || formData.target_client_ids.length > 0) &&
              'この内容で送信してもよろしいですか？'}
          </div>
          <div className={styles.btnContainer}>
            <ActionButton
              content='キャンセル'
              className={styles.addBtn}
              action={() => setIsOpen(false)}
            />
            <ActionButton
              content='送信'
              type='dark'
              className={styles.addBtn}
              action={onSubmit}
              disable={
                !formData ||
                (formData.deliveryTarget === 'manual' && formData.target_client_ids.length === 0) ||
                (formData.deliveryTarget === 'group' && !formData.selectedGroupId)
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
